// Local Employment Support Services Data
export const EmploymentSupportData = [
  {
    id: "cv-interview-support",
    title: "CV & Interview Support",
    shortDescription: "Professional guidance to showcase your strengths",
    description:
      "One-to-one support to create compelling CVs and cover letters that highlight your unique experiences, including non-traditional backgrounds and transferable skills.",
    serviceType: "cv-interview",
    whoThisHelps: [
      "Job seekers with employment gaps",
      "Career changers",
      "First-time job applicants",
      "Those with international qualifications",
      "Asylum seekers entering UK job market",
    ],
    whatWeOffer: [
      {
        service: "CV Writing & Review",
        description:
          "Create professional CVs that showcase skills from ALL experiences - parenting, volunteering, international work, and more",
        format: "1-to-1 appointments or drop-in sessions",
      },
      {
        service: "Cover Letter Crafting",
        description:
          "Tailored letters that tell your unique story and connect to employer needs",
        format: "Guided templates and personalized feedback",
      },
      {
        service: "Mock Interviews",
        description:
          "Practice interviews with constructive feedback in a supportive, non-judgmental environment",
        format: "In-person or video call sessions",
      },
      {
        service: "Interview Preparation",
        description:
          "Research company, prepare answers, manage anxiety, plan logistics (childcare, transport)",
        format: "Comprehensive pre-interview coaching",
      },
      {
        service: "Application Support",
        description:
          "Help completing online applications, understanding job descriptions, and tailoring submissions",
        format: "Assisted application sessions",
      },
    ],
    successMetrics: {
      interviewsSecured: "120+ in 2024-25",
      cvReviews: "200+ annually",
      employmentRate: "45.7% of supported clients secured employment",
    },
    successStory: {
      name: "Abdul",
      background: "Asylum seeker from Afghanistan, former teacher",
      journey:
        "UK didn't recognize his teaching credentials. Struggled to explain 2-year employment gap. APT helped reframe his experience as 'resilience and adaptability.' Highlighted transferable skills from teaching. Secured role as Learning Support Assistant, now pursuing UK teaching qualification.",
      quote:
        "I felt my past didn't matter here. APT showed me how to make my story a strength, not a barrier.",
    },
    accessInfo: {
      cost: "Free for all community members",
      bookingProcess: "Call, email, or drop-in to reception",
      languages: "Support available in Arabic, Albanian, Bengali, Urdu",
      location: "APT offices + community outreach sessions",
      waitTime: "Usually available within 1 week",
    },
  },
  {
    id: "job-search-assistance",
    title: "Job Search Assistance",
    shortDescription: "Navigate the job market with expert guidance",
    description:
      "Hands-on support finding suitable opportunities, understanding the UK job market, and connecting to employers who value diversity and community experience.",
    serviceType: "job-search",
    whoThisHelps: [
      "Those unfamiliar with UK job market",
      "Long-term unemployed",
      "People lacking digital job search skills",
      "Those without professional networks in UK",
    ],
    whatWeOffer: [
      {
        service: "Job Market Navigation",
        description:
          "Understanding UK sectors, realistic salary expectations, recognizing job scams, using major job boards",
        delivery: "Group workshops and 1-to-1 guidance",
      },
      {
        service: "Online Job Search Skills",
        description:
          "Using Indeed, Reed, LinkedIn, Universal Jobmatch, company websites - with digital support for beginners",
        delivery: "Computer lab sessions with assistance",
      },
      {
        service: "Employer Connections",
        description:
          "Direct introductions to local employers actively hiring, including those offering flexible hours for parents and carers",
        delivery: "Monthly employer engagement events",
      },
      {
        service: "Application Tracking Support",
        description:
          "Systems to organize applications, follow-ups, and manage multiple opportunities",
        delivery: "Templates and organizational tools",
      },
      {
        service: "Rights & Advocacy",
        description:
          "Understanding your employment rights, work authorization, reasonable accommodations, and challenging discrimination",
        delivery: "Information sessions and referrals",
      },
    ],
    partnerships: {
      employerNetwork: [
        "Local NHS trusts",
        "Care sector employers",
        "Retail and hospitality chains",
        "Social enterprises",
        "Local councils",
      ],
      communityPartners: [
        "Newham Workplace",
        "Integration Hubs",
        "Local job centers",
      ],
    },
    successMetrics: {
      jobsSecured: "16 secured employment (35 actively seeking)",
      averageTimeToEmployment: "3-6 months",
      employmentRetention: "75% still employed after 6 months",
    },
    successStory: {
      name: "Blessing",
      background: "Single mother from Nigeria, 5 years unemployed",
      journey:
        "Needed flexible work to care for disabled son. APT identified care sector opportunities with part-time hours. Provided Health & Safety certification. Connected to local care home. Now working 20 hours/week with supportive employer.",
      quote:
        "I applied to 100+ jobs alone and got nowhere. APT understood I needed flexibility, not just any job. They found employers who valued my circumstances.",
    },
    accessInfo: {
      cost: "Free",
      bookingProcess: "Drop-in sessions Tuesdays & Thursdays 10am-2pm",
      computerAccess: "Computer lab available for job searching",
      transportSupport: "Travel cost assistance available for interviews",
    },
  },
  {
    id: "skills-assessment",
    title: "Skills Assessment & Career Planning",
    shortDescription: "Discover your strengths and chart your path",
    description:
      "Comprehensive assessment of your skills, experiences, and aspirations to create a realistic, achievable career plan that honors your circumstances.",
    serviceType: "assessment",
    whoThisHelps: [
      "Those unsure of their career direction",
      "People wanting to change careers",
      "Individuals with skills from non-traditional sources",
      "Those needing qualification equivalency guidance",
    ],
    whatWeOffer: [
      {
        service: "Holistic Skills Inventory",
        description:
          "Identifying skills from ALL life experiences - parenting, volunteering, community leadership, international careers, caregiving",
        process:
          "Guided reflection exercises and professional assessment tools",
      },
      {
        service: "Qualification Mapping",
        description:
          "Understanding how international qualifications translate to UK standards, identifying gaps, planning next steps",
        process: "UK NARIC guidance and progression planning",
      },
      {
        service: "Career Pathway Planning",
        description:
          "Realistic goal-setting considering your circumstances (caring responsibilities, language level, financial constraints, work authorization)",
        process: "Multi-session career counseling",
      },
      {
        service: "Training Needs Analysis",
        description:
          "Identifying which courses, certifications, or experiences you need to reach your goals - with funding advice",
        process: "Gap analysis and resource mapping",
      },
      {
        service: "Barrier Assessment & Problem-Solving",
        description:
          "Honestly addressing obstacles (childcare, transport, finances, mental health) and creating solutions together",
        process: "Action planning with referrals to support services",
      },
    ],
    outcomes: {
      assessmentsCompleted: "85+ in 2024-25",
      trainingSectionReferrals: "60% enrolled in APT courses",
      employmentPathways: "45% progressed to employment within 12 months",
    },
    successStory: {
      name: "Maria",
      background: "Albanian mother of 4, former accountant",
      journey:
        "Couldn't practice accounting without UK qualifications (cost prohibitive). Skills assessment revealed strong organizational and numerical skills. Suggested Business Admin route as stepping stone. Now working as Admin Assistant, employer supporting AAT qualification.",
      quote:
        "I thought I had to start from zero. APT showed me I had valuable skills and helped me find a realistic path forward.",
    },
    accessInfo: {
      cost: "Free initial assessment",
      duration: "2-3 sessions over 4-6 weeks",
      followUp: "Ongoing support for 12 months",
      languages: "Interpreters available on request",
    },
  },
  {
    id: "success-stories",
    title: "Success Stories",
    shortDescription: "Real journeys of community members who've thrived",
    description:
      "Stories from your neighbors - asylum seekers, single parents, career changers, long-term unemployed - who found their path with APT support.",
    serviceType: "inspiration",
    categories: [
      {
        category: "From Asylum to Employment",
        stories: [
          {
            name: "Amira (Syria)",
            before: "Arrived 2023, no English, 3 children, no qualifications recognized",
            support: "ESOL Entry 3, Life in UK, CV support, childcare assistance",
            after: "Community center volunteer, supporting other refugees",
            timeline: "18 months",
            quote:
              "APT gave me language, confidence, and a community. I want to give back what I received.",
          },
          {
            name: "Abdul (Afghanistan)",
            before: "Former teacher, credentials not recognized, 2-year gap",
            support:
              "Skills assessment, CV reframing, interview prep, qualification mapping",
            after: "Learning Support Assistant, pursuing UK teaching certification",
            timeline: "8 months",
            quote: "They showed me my experience was a strength, not a barrier.",
          },
        ],
      },
      {
        category: "Single Parents Returning to Work",
        stories: [
          {
            name: "Blessing (Nigeria)",
            before: "5 years unemployed, caring for disabled son, needed flexibility",
            support:
              "Job search targeting flexible employers, Health & Safety cert, employer introductions",
            after: "Part-time care worker (20hrs/week), supportive employer",
            timeline: "4 months",
            quote:
              "They understood I needed a job that fit my life, not the other way around.",
          },
          {
            name: "Sarah (UK)",
            before: "3 years out of work, single mum, low confidence",
            support:
              "Customer Service Level 2, confidence building, work placement",
            after:
              "Part-time care home role, now pursuing Level 3 Health & Social Care",
            timeline: "10 months",
            quote: "I never thought I could work in care, but APT believed in me.",
          },
        ],
      },
      {
        category: "Career Changers Finding New Paths",
        stories: [
          {
            name: "Mohammed (UK)",
            before: "Former taxi driver, wanted stable career with progression",
            support:
              "Functional Skills Maths Level 2, career planning, apprenticeship applications",
            after: "Electrical apprentice with local contractor",
            timeline: "6 months",
            quote: "Evening classes fit around my life. Now I have a real career path.",
          },
          {
            name: "Maria (Albania)",
            before: "Former accountant, qualifications too expensive to convert",
            support:
              "Skills assessment, Business Admin pathway, employer connections",
            after: "Admin Assistant, employer funding AAT qualification",
            timeline: "7 months",
            quote: "APT helped me find a realistic path, not an impossible dream.",
          },
        ],
      },
    ],
    impactStatistics: {
      totalReached2024: "750+",
      activeParticipants: "200+",
      employmentSuccesses: "16 (45.7% of job seekers)",
      educationalAchievements: "100+ qualifications awarded",
      communitySupport: "150+ advocacy and family support cases",
    },
    callToAction:
      "Your story could be next. Every success started with one conversation.",
    accessInfo: {
      shareYourStory:
        "If APT supported you and you'd like to inspire others, contact us to share your journey.",
      privacyNote:
        "All names and details shared with permission. Some details changed to protect privacy.",
    },
  },
];

// Quick reference for navigation
export const EmploymentSupportCategories = [
  {
    id: "cv-interview-support",
    title: "CV & Interview Support",
    url: "/employment-support/cv-interview-support",
  },
  {
    id: "job-search-assistance",
    title: "Job Search Assistance",
    url: "/employment-support/job-search-assistance",
  },
  {
    id: "skills-assessment",
    title: "Skills Assessment",
    url: "/employment-support/skills-assessment",
  },
  {
    id: "success-stories",
    title: "Success Stories",
    url: "/employment-support/success-stories",
  },
];
